/**
 * Client-side AI service for LLM integration
 * Handles direct API calls to AI providers using user's API keys
 */

import { ChatMessage, AIProvider, ToolCall } from '@/types/ai';

interface AIServiceConfig {
  provider: AIProvider;
  apiKey: string;
  endpoint: string;
  model: string;
}

interface ChatCompletionRequest {
  model: string;
  messages: ChatMessage[];
  tools?: any[];
  tool_choice?: string | { type: string; function: { name: string } };
  temperature?: number;
  max_tokens?: number;
}

interface OpenAIResponse {
  id: string;
  choices: Array<{
    message: {
      role: string;
      content: string | null;
      tool_calls?: ToolCall[];
    };
    finish_reason: string;
  }>;
  usage?: {
    prompt_tokens: number;
    completion_tokens: number;
    total_tokens: number;
  };
}

interface AnthropicResponse {
  id: string;
  content: Array<{
    type: string;
    text?: string;
    id?: string;
    name?: string;
    input?: any;
  }>;
  stop_reason: string;
  usage?: {
    input_tokens: number;
    output_tokens: number;
  };
}

export class AIService {
  private config: AIServiceConfig;

  constructor(config: AIServiceConfig) {
    this.config = config;
  }

  /**
   * Send a chat completion request to the AI provider
   * Routes through server-side proxy to avoid CORS issues
   */
  async chatCompletion(
    messages: ChatMessage[],
    tools?: any[],
    toolChoice?: string | { type: string; function: { name: string } }
  ): Promise<ChatMessage> {
    // Use server-side proxy endpoint
    const response = await fetch('/api/ai/chat', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        messages,
        tools,
        toolChoice,
      }),
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.error || `API error: ${response.status}`);
    }

    const data: OpenAIResponse = await response.json();
    const choice = data.choices[0];

    return {
      role: 'assistant',
      content: choice.message.content || '',
      tool_calls: choice.message.tool_calls,
    };
  }

  /**
   * OpenAI-compatible chat completion
   */
  private async openAIChatCompletion(
    messages: ChatMessage[],
    tools?: any[],
    toolChoice?: string | { type: string; function: { name: string } }
  ): Promise<ChatMessage> {
    const endpoint = `${this.config.endpoint}/chat/completions`;
    
    const requestBody: ChatCompletionRequest = {
      model: this.config.model,
      messages,
      temperature: 0.7,
      max_tokens: 4096,
    };

    if (tools && tools.length > 0) {
      requestBody.tools = tools;
      if (toolChoice) {
        requestBody.tool_choice = toolChoice;
      }
    }

    const response = await fetch(endpoint, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${this.config.apiKey}`,
      },
      body: JSON.stringify(requestBody),
    });

    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`OpenAI API error: ${response.status} - ${errorText}`);
    }

    const data: OpenAIResponse = await response.json();
    const choice = data.choices[0];

    return {
      role: 'assistant',
      content: choice.message.content || '',
      tool_calls: choice.message.tool_calls,
    };
  }

  /**
   * Anthropic Claude chat completion
   * Note: Anthropic has a different API format, we need to adapt it
   */
  private async anthropicChatCompletion(
    messages: ChatMessage[],
    tools?: any[]
  ): Promise<ChatMessage> {
    const endpoint = `${this.config.endpoint}/messages`;
    
    // Extract system message if present
    const systemMessage = messages.find(m => m.role === 'system');
    const nonSystemMessages = messages.filter(m => m.role !== 'system');
    
    // Convert messages to Anthropic format
    const anthropicMessages = nonSystemMessages.map(msg => {
      if (msg.role === 'tool') {
        return {
          role: 'user',
          content: [{
            type: 'tool_result',
            tool_use_id: msg.tool_call_id,
            content: msg.content
          }]
        };
      }
      
      if (msg.tool_calls) {
        // Convert tool calls to Anthropic format
        const content: any[] = [];
        if (msg.content) {
          content.push({ type: 'text', text: msg.content });
        }
        msg.tool_calls.forEach(tc => {
          content.push({
            type: 'tool_use',
            id: tc.id,
            name: tc.function.name,
            input: JSON.parse(tc.function.arguments)
          });
        });
        return { role: 'assistant', content };
      }
      
      return {
        role: msg.role === 'assistant' ? 'assistant' : 'user',
        content: msg.content
      };
    });

    const requestBody: any = {
      model: this.config.model,
      messages: anthropicMessages,
      max_tokens: 4096,
      temperature: 0.7,
    };

    if (systemMessage) {
      requestBody.system = systemMessage.content;
    }

    if (tools && tools.length > 0) {
      requestBody.tools = tools.map(t => ({
        name: t.function.name,
        description: t.function.description,
        input_schema: t.function.parameters
      }));
    }

    const response = await fetch(endpoint, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'x-api-key': this.config.apiKey,
        'anthropic-version': '2023-06-01',
      },
      body: JSON.stringify(requestBody),
    });

    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`Anthropic API error: ${response.status} - ${errorText}`);
    }

    const data: AnthropicResponse = await response.json();
    
    // Convert Anthropic response back to OpenAI format
    let content = '';
    const toolCalls: ToolCall[] = [];
    
    data.content.forEach(block => {
      if (block.type === 'text' && block.text) {
        content += block.text;
      } else if (block.type === 'tool_use') {
        toolCalls.push({
          id: block.id!,
          type: 'function',
          function: {
            name: block.name!,
            arguments: JSON.stringify(block.input)
          }
        });
      }
    });

    return {
      role: 'assistant',
      content,
      tool_calls: toolCalls.length > 0 ? toolCalls : undefined,
    };
  }

  /**
   * Test the API connection
   */
  async testConnection(): Promise<boolean> {
    try {
      const testMessage: ChatMessage[] = [
        { role: 'user', content: 'Reply with just "OK" if you can read this.' }
      ];
      
      const response = await this.chatCompletion(testMessage);
      return response.content.trim().toUpperCase().includes('OK');
    } catch (error) {
      console.error('AI connection test failed:', error);
      return false;
    }
  }
}

/**
 * Create an AI service instance from user settings
 */
export function createAIService(
  provider: AIProvider,
  apiKey: string,
  endpoint: string,
  model: string
): AIService {
  return new AIService({
    provider,
    apiKey,
    endpoint,
    model,
  });
}
